import { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { Drawer, Grid, Button, Tooltip, IconButton, Checkbox, FormControlLabel, Typography, Divider, Badge, Box, Skeleton } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import AnalyticsDateFilter from './AnalyticsDateFilter';
import TextFilter from '../../../shared_elements/components/FilterComponent/TextFilter';
import NumberFilter from '../../../shared_elements/components/FilterComponent/NumberFilter';
import AnalyticsDropdownFilter from './AnalyticsDropdownFilter';
import AnalyticsFilterOutput from './AnalyticsFilterOutput';
import { InfoOutlined } from '@mui/icons-material'

const removeEmptyKey = (obj) =>
    Object.fromEntries(
        Object.entries(obj).filter(([, v]) => v !== null && v !== undefined && v !== '')
    );

const AnalyticsFilterComponent = ({
    icon, direction = 'right', tooltipContent = 'Filter', filter, filterMenu, getResponseBack, module, className = '',
    title, titleLoading = false, titleClassName = 'ca-card-title', headerClassName = 'ca-card-header', actions, disabled = false,
}) => {
    const { leaseBasicDetails } = useSelector((state) => state.leases);
    const [filterOption, setFilterOption] = useState({});
    const [isOpen, setOpen] = useState(false);
    const [error, setError] = useState({});
    const activeFilterCount = filter ? Object.keys(removeEmptyKey(filter)).length : 0;
    const pendingCount = Object.keys(removeEmptyKey(filterOption)).length;
    const hasFilterChips = !!(filter && Object.keys(filter).length);

    const onFieldChange = (keyParam, value) => {
        const inputType = filterMenu[keyParam]?.inputType;

        let updated = {
            ...filterOption,
            [keyParam]: inputType === 'text' ? value.trimStart() : value,
        };

        const dependentFields = filterMenu[keyParam]?.dependentFields;
        if (dependentFields?.length) {
            dependentFields.forEach((field) => {
                delete updated[field];
            });
        }

        setFilterOption(updated);
        setError((prev) => ({ ...prev, [keyParam]: '' }));
    };

    const onFocusChange = (keyParam) => setError((prev) => ({ ...prev, [keyParam]: '' }));

    const validateDates = () => {
        const validationErrors = {};
        const leaseStartDate = leaseBasicDetails?.start_date ? moment(leaseBasicDetails.start_date) : null;
        const leaseEndDate = leaseBasicDetails?.end_date ? moment(leaseBasicDetails.end_date) : null;
        Object.keys(filterMenu).forEach((keyParam) => {
            const config = filterMenu[keyParam];
            const value = filterOption[keyParam];
            if (config?.inputType !== 'date' || !value) return;

            const date = moment(value);
            if (!date.isValid()) {
                validationErrors[keyParam] = `Please select a valid ${config.title}`;
                return;
            }
            if (leaseStartDate?.isValid() && leaseEndDate?.isValid() && !date.isBetween(leaseStartDate, leaseEndDate, 'day', '[]')) {
                validationErrors[keyParam] = `${config.title} must be within the lease period`;
                return;
            }
            if (config.maxDateKey && filterOption[config.maxDateKey]) {
                const maxDate = moment(filterOption[config.maxDateKey]);
                if (maxDate.isValid() && date.isAfter(maxDate)) {
                    validationErrors[keyParam] = `${config.title} cannot be after ${filterMenu[config.maxDateKey]?.title}`;
                }
            }
            if (config.minDateKey && filterOption[config.minDateKey]) {
                const minDate = moment(filterOption[config.minDateKey]);
                if (minDate.isValid() && date.isBefore(minDate)) {
                    validationErrors[keyParam] = `${config.title} cannot be before ${filterMenu[config.minDateKey]?.title}`;
                }
            }
        });
        return validationErrors;
    };

    const onRemoveFilter = (key) => {
        if (key === 'all') {
            const preserved = Object.fromEntries(
                Object.entries(filter || {}).filter(([k]) => filterMenu[k]?.clearable === false)
            );
            setFilterOption(preserved);
            getResponseBack(preserved);
        } else if (filterMenu[key]?.clearable === false) {
            return;
        } else {
            const newFilter = { ...filter };
            delete newFilter[key];
            setFilterOption(newFilter);
            getResponseBack(newFilter);
        }
    };

    const handleOpen = () => {
        setOpen(true);
        setFilterOption(filter);
        setError({});
    };
    const handleClose = () => setOpen(false);
    const handleApply = () => {
        const validationErrors = validateDates();
        if (Object.values(validationErrors).some(Boolean)) {
            setError(validationErrors);
            return;
        }
        const result = getResponseBack(removeEmptyKey(filterOption));
        if (result !== false) {
            setError({});
            handleClose();
        }
    };

    return (
      <div className={headerClassName}>
            <div className={`filter-chip-row ${className}`} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '6px', width: '100%', minWidth: 0 }}>
          {titleLoading ? (
            <Skeleton variant="text" width={160} height={22} animation="wave" />
          ) : (
                    <Typography className={titleClassName} sx={{ flexShrink: 0 }}>{title}</Typography>
          )}

                {actions ? <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0, marginLeft: 'auto' }}>{actions}</Box> : null}

          {!disabled && (
            <Tooltip title={tooltipContent} arrow>
                    <span style={{ flexShrink: 0, marginLeft: actions ? 0 : 'auto' }}>
                    <IconButton size='small' color='primary' onClick={handleOpen} sx={{ marginTop: '-4px' }}>
              <Badge
                badgeContent={activeFilterCount}
                            color='primary'
                            overlap='circular'
                            sx={{ '& .MuiBadge-badge': { fontSize: '10px', minWidth: '16px', height: '16px', padding: '0 4px' } }}
              >
                            {icon || <FilterListIcon fontSize='small' />}
              </Badge>
            </IconButton>
                    </span>
            </Tooltip>
          )}

          {hasFilterChips ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', flexBasis: '100%', width: '100%', marginTop: '4px' }}>
              <AnalyticsFilterOutput
                filter={filter}
                filterMenu={filterMenu}
                onRemoveFilter={onRemoveFilter}
                module={module}
              />
                    </div>
          ) : null}
        </div>

        <Drawer
          anchor={direction}
          open={isOpen}
          onClose={handleClose}
                slotProps={{ paper: { sx: { width: 350, display: 'flex', flexDirection: 'column', overflow: 'hidden' } } }}
        >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.2, borderBottom: '1px solid #e0e0e0', bgcolor: '#f5f7ff', flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontFamily: 'Roboto-SemiBold', fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>
                Filters
              </Typography>
              {activeFilterCount > 0 && (
                            <Box sx={{ bgcolor: '#1976d2', color: '#fff', borderRadius: '10px', px: 0.8, fontSize: '11px', fontWeight: 700, lineHeight: '18px', minWidth: '18px', textAlign: 'center' }}>
                  {activeFilterCount}
                </Box>
              )}
            </Box>
                    <IconButton size='small' onClick={handleClose} sx={{ color: '#666', '&:hover': { bgcolor: '#ebebeb' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pt: 1, pb: 1 }}>
            <Grid container spacing={0}>
              {Object.keys(filterMenu).map((keyParam, index) => {
                            const { inputType, md, disabledWhen, disabled: staticDisabled, visibleWhen } = filterMenu[keyParam];
                            if (visibleWhen && !visibleWhen(filterOption)) return null;
                            const isDisabled = (disabledWhen ? disabledWhen(filterOption) : false) || !!staticDisabled;

                            if (inputType === 'text') {
                  return (
                    <Grid size={{ xs: 12 }} key={index}>
                      <TextFilter
                        filterOption={filterOption}
                        filterMenu={filterMenu}
                        keyParam={keyParam}
                        onFieldChange={onFieldChange}
                        placeHolder={filterMenu[keyParam].placeHolder}
                      />
                    </Grid>
                  );
                }

                            if (inputType === 'number') {
                  return (
                    <Grid size={{ xs: md || 12 }} key={index}>
                      <NumberFilter
                        filterOption={filterOption}
                        filterMenu={filterMenu}
                        keyParam={keyParam}
                        onFieldChange={onFieldChange}
                        placeHolder={filterMenu[keyParam].placeHolder}
                      />
                    </Grid>
                  );
                }

                            if (inputType === 'dropdown') {
                  return (
                    <Grid size={{ xs: 12 }} key={index}>
                      <AnalyticsDropdownFilter
                        filterOption={filterOption}
                        filterMenu={filterMenu}
                        keyParam={keyParam}
                        onFieldChange={onFieldChange}
                        placeHolder={filterMenu[keyParam].placeHolder}
                        disabled={isDisabled}
                      />
                    </Grid>
                  );
                }

                            if (inputType === 'date') {
                  return (
                    <Grid size={{ xs: 12 }} key={index}>
                      <AnalyticsDateFilter
                        filterOption={filterOption}
                        filterMenu={filterMenu}
                        keyParam={keyParam}
                        onFieldChange={onFieldChange}
                        onFocusChange={onFocusChange}
                        error={error[keyParam]}
                      />
                    </Grid>
                  );
                }

                            if (inputType === 'checkbox') {
                  return (
                    <Grid size={{ xs: 12 }} key={index}>
                      <FormControlLabel
                                            sx={{ mt: 0.5, mb: 0, height:'15px' }}
                        control={
                          <Checkbox
                                                    size='small'
                                                    color='primary'
                            checked={!!filterOption[keyParam]}
                            onChange={(e) => {
                              if (e.target.checked) {
                                onFieldChange(keyParam, true);
                              } else {
                                setFilterOption((prev) => {
                                  const updated = { ...prev };
                                  delete updated[keyParam];
                                  return updated;
                                });
                              }
                            }}
                          />
                        }
                                            label={<Typography sx={{ fontSize: '13px', fontFamily: 'Roboto-Regular' }}>{filterMenu[keyParam].title}</Typography>}
                      />
                    </Grid>
                  );
                }

                return null;
              })}
            </Grid>
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, mt: 1.5, px: 1, py: 0.5, borderRadius: 2, bgcolor: "#FFF8E1", border: "1px solid #FFE082", color: "#8A6D1D", }} >
              <InfoOutlined sx={{ fontSize: 16 }} />
              <Typography fontSize="11px">
                By default, data from the last <strong>1 year</strong> is
                displayed when no date filters are applied.
              </Typography>
            </Box>
          </Box>

          <Divider />
                <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, flexShrink: 0, bgcolor: '#fafafa' }}>
            {pendingCount > 0 ? (
              <Button
                fullWidth
                onClick={handleApply}
                            color='primary'
                            variant='contained'
                            size='small'
                            sx={{ fontFamily: 'Roboto-SemiBold', fontSize: '13px', textTransform: 'none', py: 0.75, borderRadius: '4px' }}
              >
                Apply ({pendingCount})
              </Button>
            ) : null}
            <Button
              fullWidth
              onClick={handleClose}
                        variant='outlined'
                        color='primary'
                        size='small'
                        sx={{ fontFamily: 'Roboto-SemiBold', fontSize: '13px', textTransform: 'none', py: 0.75, borderRadius: '4px'}}
            >
              Cancel
            </Button>
          </Box>
        </Drawer>
      </div>
    );
};

export default AnalyticsFilterComponent;
